import type { GalleryTemplateId } from '@reflex/flows';

/** A required plugin the caller org must install before a template is usable. */
export interface FlowTemplateMissingPlugin {
  /** Install record / plugin name (e.g. `linear`). */
  pluginName: string;
  /** Short human label for the install prompt (e.g. `Linear`). */
  displayName: string;
}

/**
 * Availability of one gallery template for the caller org (`GET /flows/templates`).
 * Hidden templates — the org can't satisfy a required plugin's gate — are omitted,
 * so every entry is at least previewable.
 */
export interface FlowTemplateAvailability {
  /** Catalog id; matches `@reflex/flows` `GALLERY_TEMPLATES`. */
  id: GalleryTemplateId;
  /** `usable` — every required plugin installed; `preview` — at least one not installed. */
  availability: 'usable' | 'preview';
  /** Required-but-not-installed plugins; present only when `availability` is `preview`. */
  missing?: FlowTemplateMissingPlugin[];
}

/**
 * Short label for a plugin name (`linear` → `Linear`). `InstallationView` exposes
 * only the lowercase name, so this first-letter title-cases — fine for single-word
 * names but not casing-sensitive ones (`github` → `Github`). `linear` is the only
 * gated plugin today; source a real label if a mis-cased one ships.
 */
export function pluginDisplayName(pluginName: string): string {
  return pluginName.charAt(0).toUpperCase() + pluginName.slice(1);
}
